import { useEffect, useState } from 'react'

import { API, API_URL } from '../../../api'
import { guidedChoiceOffer } from './pricebook-display'

/** Loads the current retail offer and Terms of Service link and starts Stripe checkout. */
export function useRetailCheckout(createRetailCheckoutSession, { enabled = true } = {}) {
  // Undefined means loading; null means unavailable.
  const [offer, setOffer] = useState(undefined)
  const [termsOfServiceLink, setTermsOfServiceLink] = useState(null)
  const [checkoutStatus, setCheckoutStatus] = useState('idle')

  useEffect(() => {
    // Clear stale offer and checkout state when the flow is disabled or restarted.
    setOffer(undefined)
    setCheckoutStatus('idle')
    if (!enabled) return undefined

    let active = true
    const loadOffer = async () => {
      try {
        const { data } = await API.get('pricebook/available')
        if (active) setOffer(guidedChoiceOffer(data) ?? null)
      } catch (err) {
        console.error(err)
        if (active) setOffer(null)
      }
    }

    loadOffer()

    return () => {
      active = false
    }
  }, [enabled])

  useEffect(() => {
    setTermsOfServiceLink(null)
    if (!enabled) return undefined

    let active = true
    const loadTermsLink = async () => {
      try {
        const { data } = await API.get('/termsAndConditions/docs/default')
        const terms = data?.find(doc => doc.docDescription === 'Terms of Service')

        if (terms && active) {
          setTermsOfServiceLink(`${API_URL}termsAndConditions/default?docId=${terms.id}`)
        }
      } catch (err) {
        console.error(err)
      }
    }

    loadTermsLink()

    return () => {
      active = false
    }
  }, [enabled])

  const offerUnavailable = offer === null

  const startCheckout = async () => {
    if (!offer) return

    setCheckoutStatus('pending')

    try {
      const stripeUrl = await createRetailCheckoutSession(offer.pricebook.id)
      if (!stripeUrl) {
        throw new Error('checkout-session/create returned no url')
      }
      window.open(stripeUrl, '_self')
    } catch (err) {
      console.error(err)
      setCheckoutStatus('failed')
    }
  }

  return { offer, offerUnavailable, termsOfServiceLink, checkoutStatus, startCheckout }
}
